import { AdminLayout } from "./AdminLayout";
import { Plus, Search, Mail, Clock, User, Building2, MoreVertical, Tag, CheckSquare, Link2, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Inbox = () => {
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  
  const [formData, setFormData] = useState({
    subject: '',
    participants: '',
    external_thread_id: '',
    provider: 'gmail',
    contact_id: '',
    company_id: '',
    status: 'unread'
  });

  const fetchThreads = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/inbox/threads`, { params, withCredentials: true });
      setThreads(response.data);
    } catch (error) {
      toast.error("Failed to fetch email threads");
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/crm/contacts`, { withCredentials: true });
      setContacts(response.data);
    } catch (error) {
      console.error("Failed to fetch contacts");
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/crm/companies`, { withCredentials: true });
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies");
    }
  };

  useEffect(() => {
    fetchThreads();
    fetchContacts();
    fetchCompanies();
  }, [statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        participants: formData.participants.split(',').map(p => p.trim()).filter(Boolean)
      };
      await axios.post(`${API}/inbox/threads`, data, { withCredentials: true });
      toast.success("Email thread linked");
      setIsDialogOpen(false);
      setFormData({
        subject: '', participants: '', external_thread_id: '', provider: 'gmail',
        contact_id: '', company_id: '', status: 'unread'
      });
      fetchThreads();
    } catch (error) {
      toast.error("Failed to link email thread");
    }
  };

  const updateStatus = async (threadId, status) => {
    try {
      await axios.put(`${API}/inbox/threads/${threadId}`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchThreads();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (threadId) => {
    if (!window.confirm('Delete this email thread?')) return;
    try {
      await axios.delete(`${API}/inbox/threads/${threadId}`, { withCredentials: true });
      toast.success("Thread deleted");
      fetchThreads();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!selectedThread) return;
    try {
      await axios.post(`${API}/inbox/threads/${selectedThread.thread_id}/create-task`, null, {
        params: { title: taskTitle },
        withCredentials: true
      });
      toast.success("Task created from email");
      setTaskDialogOpen(false);
      setTaskTitle('');
      setSelectedThread(null);
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const statusColors = {
    unread: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    waiting: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    done: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  const statusLabels = {
    unread: 'Unread',
    in_progress: 'In Progress',
    waiting: 'Waiting',
    done: 'Done'
  };

  const filteredThreads = threads.filter(thread =>
    thread.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    thread.participants?.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.contact_id === contactId);
    return contact?.name || null;
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.name || null;
  };

  return (
    <AdminLayout title="Comms">
      <div className="space-y-6" data-testid="admin-inbox">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Communications</h2>
            <p className="text-sm text-[#8A8A8A]">Email threads linked to pipeline contacts</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Link Email Thread
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E] max-w-lg">
              <DialogHeader>
                <DialogTitle>Link Email Thread</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input 
                  type="text" 
                  placeholder="Email Subject *" 
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="input-field"
                />
                <input 
                  type="text" 
                  placeholder="Participants (comma-separated emails)"
                  value={formData.participants}
                  onChange={(e) => setFormData({...formData, participants: e.target.value})}
                  className="input-field"
                />
                <div className="grid grid-cols-2 gap-4">
                  <select 
                    value={formData.provider}
                    onChange={(e) => setFormData({...formData, provider: e.target.value})}
                    className="input-field"
                  >
                    <option value="gmail">Gmail</option>
                    <option value="outlook">Outlook/O365</option>
                    <option value="manual">Manual Entry</option>
                  </select>
                  <input 
                    type="text" 
                    placeholder="External Thread ID"
                    value={formData.external_thread_id}
                    onChange={(e) => setFormData({...formData, external_thread_id: e.target.value})}
                    className="input-field"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <select 
                    value={formData.contact_id}
                    onChange={(e) => setFormData({...formData, contact_id: e.target.value})}
                    className="input-field"
                  >
                    <option value="">Link to Contact...</option>
                    {contacts.map(contact => (
                      <option key={contact.contact_id} value={contact.contact_id}>{contact.name}</option>
                    ))}
                  </select>
                  <select 
                    value={formData.company_id}
                    onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                    className="input-field"
                  >
                    <option value="">Link to Company...</option>
                    {companies.map(company => (
                      <option key={company.company_id} value={company.company_id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">Link Thread</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Banner */}
        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-4 text-sm">
          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 text-[#5F8FB8] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-[#F5F4EF]">Link-based email tracking</div>
              <p className="text-[#6B7280] mt-1">
                Link email threads from Gmail or Outlook to pipeline contacts. Track status, create tasks from threads.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input 
              type="text" 
              placeholder="Search threads..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10" 
            />
          </div>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-full sm:w-48"
          >
            <option value="">All Status</option>
            <option value="unread">Unread</option>
            <option value="in_progress">In Progress</option>
            <option value="waiting">Waiting</option>
            <option value="done">Done</option>
          </select>
        </div>

        {/* Threads List */}
        <div className="space-y-2">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="bg-[#1C1C1C] border border-[#2E2E2E] p-4">
                <div className="h-4 w-3/4 bg-[#2E2E2E] rounded animate-pulse" />
              </div>
            ))
          ) : filteredThreads.length === 0 ? (
            <div className="text-center py-12 text-[#8A8A8A] border border-[#2E2E2E]">
              No email threads linked yet
            </div>
          ) : (
            filteredThreads.map((thread) => (
              <div 
                key={thread.thread_id} 
                className={`bg-[#1C1C1C] border border-[#2E2E2E] p-4 hover:border-[#5F8FB8]/30 transition-colors ${thread.status === 'unread' ? 'border-l-4 border-l-blue-500' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={`font-medium truncate ${thread.status === 'unread' ? 'text-[#F5F4EF]' : 'text-[#8A8A8A]'}`}>
                        {thread.subject}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs border ${statusColors[thread.status]}`}>
                        {statusLabels[thread.status]}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-xs text-[#8A8A8A]">
                      {thread.participants?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {thread.participants.slice(0, 2).join(', ')}
                          {thread.participants.length > 2 && ` +${thread.participants.length - 2}`}
                        </span>
                      )}
                      {thread.contact_id && getContactName(thread.contact_id) && (
                        <span className="flex items-center gap-1 text-[#5F8FB8]">
                          <User className="w-3 h-3" />
                          {getContactName(thread.contact_id)}
                        </span>
                      )}
                      {thread.company_id && getCompanyName(thread.company_id) && (
                        <span className="flex items-center gap-1 text-[#5F8FB8]">
                          <Building2 className="w-3 h-3" />
                          {getCompanyName(thread.company_id)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(thread.last_message_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                      {thread.external_thread_id && (
                        <span className="flex items-center gap-1 text-[#8A8A8A]">
                          <ExternalLink className="w-3 h-3" />
                          {thread.provider}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-[#2E2E2E]">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                        <DropdownMenuItem 
                          onClick={() => { setSelectedThread(thread); setTaskTitle(`Follow up: ${thread.subject}`); setTaskDialogOpen(true); }}
                          className="cursor-pointer hover:bg-[#2E2E2E]"
                        >
                          <CheckSquare className="w-4 h-4 mr-2" /> Create Task
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(thread.thread_id, 'in_progress')} className="cursor-pointer hover:bg-[#2E2E2E]">
                          Mark In Progress
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(thread.thread_id, 'waiting')} className="cursor-pointer hover:bg-[#2E2E2E]">
                          Mark Waiting
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(thread.thread_id, 'done')} className="cursor-pointer hover:bg-[#2E2E2E]">
                          Mark Done
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(thread.thread_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Task Dialog */}
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
            <DialogHeader>
              <DialogTitle>Create Task from Email</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
              <input 
                type="text" 
                placeholder="Task Title *"
                required
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                className="input-field"
              />
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setTaskDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm">Create Task</button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default Inbox;
